import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Lightbulb, TrendingUp, Brain, Target } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

const WelcomeGuide = () => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenWelcomeGuide');
    if (!hasSeenGuide) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('hasSeenWelcomeGuide', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
    >
      <Card 
        className="max-w-2xl w-full"
        style={{ 
          background: theme.cardBg, 
          borderColor: theme.borderColor,
          backdropFilter: 'blur(20px)'
        }}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)` }}
              >
                <Lightbulb className="w-6 h-6" style={{ color: '#0A0A0B' }} />
              </div>
              <CardTitle style={{ color: theme.textPrimary, fontSize: '24px', letterSpacing: '-0.01em' }}>
                Meet Kim. Welcome to Gamma QC.
              </CardTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" style={{ color: theme.textSecondary }} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <p style={{ color: theme.textSecondary, fontSize: '15px', lineHeight: '1.65' }}>
            Kim is the Complete Economic Investment Mathematical Intuitive Engine. Warren is her voice. The Chemist is who she becomes when the analysis goes deep. Every action you log compounds into a personal record. Every verdict is cryptographically attestable.
          </p>

          <div className="space-y-3">
            <div
              className="flex gap-4 p-4 rounded-xl"
              style={{ background: `${theme.primary}08`, border: `1px solid ${theme.borderColor}` }}
            >
              <div className="flex-shrink-0">
                <Target className="w-5 h-5" style={{ color: theme.primary, marginTop: '2px' }} />
              </div>
              <div>
                <h4 className="font-semibold mb-1" style={{ color: theme.textPrimary, fontSize: '14px' }}>
                  Run analysis
                </h4>
                <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.55' }}>
                  Enter any ticker. Twelve technical indicators, earnings posture, and sentiment regime fused into a verdict with confidence.
                </p>
              </div>
            </div>

            <div
              className="flex gap-4 p-4 rounded-xl"
              style={{ background: `${theme.success}08`, border: `1px solid ${theme.borderColor}` }}
            >
              <div className="flex-shrink-0">
                <TrendingUp className="w-5 h-5" style={{ color: theme.success, marginTop: '2px' }} />
              </div>
              <div>
                <h4 className="font-semibold mb-1" style={{ color: theme.textPrimary, fontSize: '14px' }}>
                  Build a position
                </h4>
                <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.55' }}>
                  Entry, stop, target, and risk/reward — computed against your watchlist and your historical decision performance.
                </p>
              </div>
            </div>

            <div
              className="flex gap-4 p-4 rounded-xl"
              style={{ background: `${theme.accent}08`, border: `1px solid ${theme.borderColor}` }}
            >
              <div className="flex-shrink-0">
                <Brain className="w-5 h-5" style={{ color: theme.accent, marginTop: '2px' }} />
              </div>
              <div>
                <h4 className="font-semibold mb-1" style={{ color: theme.textPrimary, fontSize: '14px' }}>
                  Compound your record
                </h4>
                <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.55' }}>
                  Every decision sealed with a cryptographic receipt. Your track record becomes verifiable by any third party — including future allocators.
                </p>
              </div>
            </div>
          </div>

          {/* Orientation note — no "Pro Tip" framing, no exclamations, just guidance */}
          <div
            className="p-3 rounded-lg flex items-start gap-2.5"
            style={{ background: `${theme.primary}06`, border: `1px solid ${theme.borderColor}` }}
          >
            <Lightbulb size={14} style={{ color: theme.primary, marginTop: '2px', flexShrink: 0 }} />
            <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.55' }}>
              First time? Open the <strong style={{ color: theme.textPrimary }}>Sector Heatmap</strong> to read today's regime, then drill into individual names.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button
              onClick={handleClose}
              style={{
                background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                borderRadius: '10px',
                padding: '10px 28px',
                color: '#0A0A0B',
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              Begin
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WelcomeGuide;